export function renderGenCreateSelectOptionContentServerActionTemplate({
  ctxSelectOptionsTableVar,
  ctxCacheInvalidationFn,
  ctxCamelSingular,
  ctxSingular,
  selectOptionStaticOptions,
  ctxSingularText
}: {
  ctxSelectOptionsTableVar: string
  ctxCacheInvalidationFn: string
  ctxCamelSingular: string
  ctxSingular: string
  selectOptionStaticOptions: string
  ctxSingularText: string
}): string {
  return `'use server'

import { requireRole, UserRole } from '@admin/auth/middleware'
import db from '@admin/db'
import { ${ctxSelectOptionsTableVar} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { ${ctxCacheInvalidationFn} } from 'next/cache'
import { ${ctxCamelSingular}SelectOptionsCacheTags } from './types'
import type {
  Create${ctxSingular}SelectOptionInput,
  Create${ctxSingular}SelectOptionResult,
  ${ctxSingular}SelectOption,
  ${ctxSingular}SelectOptionFieldName
} from './types'

${selectOptionStaticOptions}

function toSelectOptionValue(label: string): string {
  const normalized = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return normalized || 'option'
}

async function resolveSelectOptionValue(
  fieldName: ${ctxSingular}SelectOptionFieldName,
  label: string
): Promise<string> {
  const baseValue = toSelectOptionValue(label)
  const rows = await db
    .select({ value: ${ctxSelectOptionsTableVar}.value })
    .from(${ctxSelectOptionsTableVar})
    .where(eq(${ctxSelectOptionsTableVar}.fieldName, fieldName))
  const usedValues = new Set([
    ...STATIC_SELECT_OPTIONS[fieldName].map((option) => option.value),
    ...rows.map((row) => row.value)
  ])

  if (!usedValues.has(baseValue)) return baseValue

  let suffix = 2
  let value = \`\${baseValue}-\${suffix}\`
  while (usedValues.has(value)) {
    suffix += 1
    value = \`\${baseValue}-\${suffix}\`
  }

  return value
}

export async function create${ctxSingular}SelectOption(
  input: Create${ctxSingular}SelectOptionInput
): Promise<Create${ctxSingular}SelectOptionResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    const label = input.label.trim()
    if (!label) {
      return { success: false, error: 'Option label is required' }
    }

    const staticMatch = STATIC_SELECT_OPTIONS[input.fieldName].find(
      (option) => option.label.toLowerCase() === label.toLowerCase()
    )
    if (staticMatch) {
      return {
        success: true,
        option: {
          id: staticMatch.value,
          fieldName: input.fieldName,
          label: staticMatch.label,
          value: staticMatch.value,
          createdAt: '',
          updatedAt: ''
        }
      }
    }

    const rows = await db
      .select()
      .from(${ctxSelectOptionsTableVar})
      .where(eq(${ctxSelectOptionsTableVar}.fieldName, input.fieldName))
    const existing = rows.find((row) => row.label.toLowerCase() === label.toLowerCase())
    if (existing) {
      return { success: true, option: existing as ${ctxSingular}SelectOption }
    }

    const actorId = actor.id
    const now = new Date().toISOString()
    const value = await resolveSelectOptionValue(input.fieldName, label)
    const [option] = await db
      .insert(${ctxSelectOptionsTableVar})
      .values({
        fieldName: input.fieldName,
        label,
        value,
        createdAt: now,
        updatedAt: now,
        createdBy: actorId,
        updatedBy: actorId
      })
      .returning()

    ${ctxCacheInvalidationFn}(${ctxCamelSingular}SelectOptionsCacheTags.byField(input.fieldName))

    return { success: true, option: option as ${ctxSingular}SelectOption }
  } catch (error) {
    console.error('Error creating ${ctxSingularText} select option:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create option'
    }
  }
}
`
}
